// Cap Table Type Definitions
// Comprehensive TypeScript interfaces for cap table management
// Based on sophisticated backend implementation from cptbl reference

import type { DocumentType } from '@/types/document';

// Enums - Defined first to be used in interfaces
export enum StakeholderType {
  FOUNDER = 'FOUNDER',
  EMPLOYEE = 'EMPLOYEE',
  INVESTOR = 'INVESTOR',
  OPTION_POOL = 'OPTION_POOL',
  CONVERTIBLE_HOLDER = 'CONVERTIBLE_HOLDER',
  ADVISOR = 'ADVISOR',
  COMPANY = 'COMPANY',
  OTHER = 'OTHER',
}

export enum ShareClassType {
  COMMON = 'common',
  PREFERRED = 'preferred',
  OPTION = 'options',
  CONVERTIBLE = 'convertible',
  WARRANT = 'warrant',
}

export enum CaptableTransactionStatus {
  DRAFT = 'DRAFT',
  APPROVED = 'APPROVED',
  POSTED = 'POSTED',
  REVERSED = 'REVERSED',
}

export enum TransactionType {
  INCORPORATION = 'incorporation',
  ISSUANCE = 'issuance',
  TRANSFER = 'transfer',
  REPURCHASE = 'repurchase',
  CANCELLATION = 'cancellation',
  CONVERSION = 'conversion',
  EXERCISE = 'exercise',
  SPLIT = 'split',
  DIVIDEND = 'dividend',
  FINANCING_ROUND = 'financing_round',
  CONVERTIBLE_ISSUANCE = 'convertible_issuance',
  WARRANT_EXERCISE = 'warrant_exercise',
  OPTION_GRANT = 'option_grant',
  REDEMPTION = 'redemption',
  SALE = 'sale',
  SHARE_ISSUANCE = 'share_issuance',
  SAFE_PRE_ISSUANCE = 'safe_pre_issuance',
  SAFE_POST_ISSUANCE = 'safe_post_issuance',
  CLA_ISSUANCE = 'cla_issuance',
  WARRANT_ISSUANCE = 'warrant_issuance',
  CONVERSION_SHARE_ISSUANCE = 'conversion_share_issuance',
  OTHER = 'other',
}

export enum InstrumentType {
  SAFE = 'safe', // Legacy
  SAFE_PRE = 'safe_pre',
  SAFE_POST = 'safe_post',
  CONVERTIBLE_NOTE = 'convertible_note',
  WARRANT = 'warrant',
  OPTION = 'option',
}

export enum ConvertibleStatus {
  DRAFT = 'draft',
  RUNNING = 'running',
  CONVERTED = 'converted',
  TERMINATED = 'terminated',
  REPAID = 'repaid',
  EXERCISED = 'exercised',
}

export interface ConvertibleDraftCreated {
  instrument_id: string;
  transaction_id: string;
}

export interface ConvertibleRepaymentDraftCreated {
  instrument_id: string;
  repayment_transaction_id: string;
}

export interface WarrantIssuanceRequest {
  stakeholder_id: string;
  issue_date: string;
  expiry_date?: string;
  // The class the terms below are denominated in, fixed at grant. A split
  // rescales one class, and adjusting the warrant's entitlement and strike
  // through it is only possible if the class is recorded.
  share_class_id: string;
  strike_price: number;
  number_of_shares: number;
  currency_id: string;
  description?: string;
  document_id?: string;
}

export interface WarrantExerciseRequest {
  share_class_id: string;
  warrant_conversion_date: string;
  document_id?: string;
}

export interface WarrantDraftCreated {
  instrument_id: string;
  transaction_id: string;
}

export interface WarrantExerciseCreated {
  transaction_id: string;
  instrument_id: string;
}

export enum WarrantSharePriceSource {
  CURRENT_VALUATION = 'current_valuation',
  LAST_ROUND = 'last_round',
  NO_PRICE = 'no_price',
}

export enum WarrantExclusionReason {
  NOT_ACTIVE = 'not_active',
  NOT_YET_GRANTED = 'not_yet_granted',
  EXPIRED = 'expired',
  EXERCISED = 'exercised',
}

// Strike and shares are split-adjusted to `valued_on`.
export interface WarrantFairValueBreakdown {
  valued_on: string;
  share_price: number;
  share_price_source: WarrantSharePriceSource;
  company_valuation?: number;
  company_valuation_date?: string;
  total_shares?: number;
  // One share of the warrant's class, in ordinary shares.
  share_class_weight: number;
  strike_price: number;
  number_of_shares: number;
  excluded_reason?: WarrantExclusionReason;
  fair_value: number;
}

export enum InterestMethod {
  SIMPLE = 'simple',
  COMPOUND = 'compound',
}

export enum AccrualFrequency {
  DAILY = 'daily',
  YEARLY = 'yearly',
}

export enum CapTableViewMode {
  OUTSTANDING = 'outstanding',
  FULLY_DILUTED = 'fully_diluted',
  AS_CONVERTED = 'as_converted',
}

/**
 * Which transaction statuses a cap-table read counts. Independent of
 * CapTableViewMode: that is the dilution axis, this is the finality one.
 */
export enum CapTableFinality {
  WORKING = 'working',
  OFFICIAL = 'official',
}

export interface CapTableViewOptions {
  view_mode?: CapTableViewMode;
  finality?: CapTableFinality;
  as_of_date?: string;
  include_convertibles?: boolean;
  include_options?: boolean;
}

// Core Interfaces
export interface Stakeholder {
  id: string;
  portfolio_company_id: string;
  name: string;
  stakeholder_type: StakeholderType;
  registration_number?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  notes?: string;
  fund_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface ShareClass {
  id: string;
  portfolio_company_id: string;
  name: string;
  class_type: ShareClassType;
  liquidation_preference?: number;
  liquidation_preference_multiple?: number;
  is_participating?: boolean;
  participation_cap_multiple?: number;
  is_cumulative_dividend?: boolean;
  dividend_rate?: number;
  anti_dilution_protection?: string;
  seniority_rank: number;
  strike_price?: number;
  exercise_period_years?: number;
  vesting_schedule?: VestingSchedule;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

interface VestingSchedule {
  cliff_months?: number;
  vesting_period_months?: number;
  acceleration_triggers?: AccelerationTrigger[];
}

interface AccelerationTrigger {
  trigger_type: string;
  acceleration_percentage: number;
  conditions?: string[];
}

export interface LedgerTransaction {
  id: string;
  portfolio_company_id: string;
  transaction_type: TransactionType;
  transaction_date: string;
  description?: string;
  round_name?: string;
  financing_round_id?: string;
  price_per_share?: number;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  total_amount?: number;
  asset_currency_id?: string;
  asset_currency_iso_code?: string;
  asset_amount?: number;
  fx_rate?: number;
  needs_fx_rate?: boolean;
  /** needs_fx_rate narrowed to rows projected into a fund base currency. */
  awaiting_fx_rate?: boolean;
  total_shares?: number;
  target_share_class_id?: string;
  // Only set for SPLIT transactions. The ratio sides stay as wire strings:
  // NUMERIC(20, 8) carries more digits than a JS number holds.
  split_share_class_id?: string;
  split_ratio_numerator?: string;
  split_ratio_denominator?: string;
  board_resolution_date?: string;
  is_board_approved?: boolean;
  legal_documents?: LegalDocument[];
  ledger_entries: ShareLedgerEntry[];
  parties: string[];
  stakeholder_count?: number;
  fund_is_party?: boolean;
  requires_approval?: boolean;
  is_editable?: boolean;
  convertible_detail?: ConvertibleInstrument;
  // An option grant's exercise price exactly as recorded, in the units of the
  // grant's own date. This is what the edit form hydrates from and writes
  // back.
  option_strike_price?: number;
  // The strike redenominated through every split of its class since - what a
  // reader displays. Detail responses only; never send it back, or it
  // restates the grant's terms.
  option_strike_price_in_current_units?: number;
  related_transactions?: RelatedTransaction[];
  status: CaptableTransactionStatus;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface RelatedTransaction {
  id: string;
  transaction_type: string;
  transaction_date: string;
  description?: string;
  status: CaptableTransactionStatus;
}

interface CaptableJournalEntryLine {
  id: string;
  account_code: string;
  account_name: string;
  debit_amount: number;
  credit_amount: number;
  currency: string;
  description: string;
  journal_entry_number: string;
}

export interface CaptableGLTransaction {
  id: string;
  fund_id: string;
  fund_name: string;
  transaction_type: string;
  transaction_date: string;
  total_amount: number;
  currency: string;
  description: string;
  reference: string | null;
  status: string;
  lines: CaptableJournalEntryLine[];
}

export interface ShareLedgerEntry {
  id: string;
  transaction_id: string;
  stakeholder_id: string;
  stakeholder_name: string;
  share_class_id: string;
  shares_delta: number;
  price_per_share?: number;
  total_amount?: number;
  certificate_number?: string;
  vesting_start_date?: string;
  vesting_end_date?: string;
  exercise_deadline?: string;
  notes?: string;
  stakeholder?: Stakeholder;
  share_class?: ShareClass;
  transaction?: LedgerTransaction;
}

export interface ConvertibleInstrument {
  id: string;
  portfolio_company_id: string;
  stakeholder_id: string;
  instrument_type: InstrumentType;
  investment_amount: number;
  currency_id: string;
  currency_iso_code: string;
  issue_date: string;
  signing_date?: string;
  transfer_date?: string;
  maturity_date?: string;
  status: ConvertibleStatus;
  interest_rate?: number;
  interest_method: InterestMethod;
  interest_accrual_frequency: AccrualFrequency;
  discount_rate?: number;
  valuation_cap?: number;
  // The terms as agreed at grant. These are the contract, and what an edit
  // round-trips.
  strike_price?: number;
  number_of_shares?: number;
  // The terms restated in today's units, which the exercise endpoints
  // validate against - a surface offering an exercise shows and sends these,
  // not the recorded pair. Absent for notes and SAFEs.
  strike_price_in_current_units?: number;
  number_of_shares_in_current_units?: number;
  coverage_percentage?: number;
  conversion_price?: number;
  conversion_shares?: number;
  is_converted: boolean;
  conversion_date?: string;
  conversion_transaction_id?: string;
  repayment_transaction_id?: string;
  issuance_transaction_id?: string;
  document_id?: string;
  most_favored_nation?: boolean;
  pro_rata_rights?: boolean;
  legal_documents?: LegalDocument[];
  notes?: string;
  stakeholder?: Stakeholder;
  stakeholder_name?: string;
  stakeholder_type?: string;
  // Computed CLA fields
  runtime_days?: number;
  accrued_interest?: number;
  nav_including_interest?: number;
  warrant_fair_value?: WarrantFairValueBreakdown;
  is_editable?: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface ConvertibleInstrumentFundDetail extends ConvertibleInstrument {
  portfolio_company_name: string;
}

interface LegalDocument {
  id: string;
  document_name: string;
  document_type: string;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  upload_date: string;
  is_executed: boolean;
  execution_date?: string;
  signatories?: Signatory[];
}

interface Signatory {
  id: string;
  name: string;
  title?: string;
  entity?: string;
  signed_at?: string;
  signature_method?: string;
}

// Cap Table Snapshot and Analysis
export interface StakeholderPosition {
  stakeholder_id: string;
  stakeholder_name: string;
  stakeholder_type: string;
  share_positions: Record<string, number>; // share_class_id -> shares
  as_converted_positions: Record<string, number>; // share_class_id -> as-converted shares
  total_shares: number;
  total_as_converted_shares: number;
  ownership_percentage: number;
  convertible_shares?: Record<string, number>; // share_class_id -> implied shares
  total_convertible_shares?: number;
  has_convertibles: boolean;
  cost_basis_by_share_class?: Record<string, number>; // share_class_id -> total cost basis
}

export interface StakeholderHolding {
  stakeholder_id: string;
  stakeholder_name: string;
  fund_id: string | null;
  fund_name: string | null;
  share_class_id: string;
  share_class_name: string;
  shares: number;
  as_converted_shares: number;
  cost_basis: number | null;
}

export interface ShareClassSummary {
  share_class_id: string;
  share_class_name: string;
  share_class_type?: string;
  outstanding_shares: number;
  as_converted_shares: number;
  convertible_shares?: number;
  total_shares: number;
  percentage_of_total: number;
}

export interface CapTablePosition {
  stakeholder_id: string;
  stakeholder_name: string;
  stakeholder_type: StakeholderType;
  share_class_id: string;
  share_class_name: string;
  shares: number;
  as_converted_shares: number;
  ownership_percentage: number;
  fully_diluted_percentage?: number;
  liquidation_preference?: number;
  cost_basis?: number;
  current_value?: number;
  unrealized_gain_loss?: number;
  vesting_details?: VestingDetails;
}

interface VestingDetails {
  total_shares: number;
  vested_shares: number;
  unvested_shares: number;
  vesting_start_date?: string;
  vesting_end_date?: string;
  cliff_date?: string;
  acceleration_triggers?: AccelerationTrigger[];
}

export interface CapTableSnapshot {
  stakeholder_positions: StakeholderPosition[];
  stakeholder_holdings: StakeholderHolding[];
  share_class_summaries: ShareClassSummary[];
  total_outstanding_shares: number;
  total_as_converted_shares: number;
  total_convertible_shares?: number;
  total_shares: number;
  portfolio_company_id: string;
  view_mode: CapTableViewMode;
  as_of_date: string;
  conversion_price?: number;
  conversion_valuation?: number;
  valuation_type?: string;
  unconverted_instruments?: ConvertibleInstrument[];
  conversion_calculations?: unknown[];
  has_unconverted_instruments: boolean;
  has_any_convertibles: boolean;
  has_priced_rounds: boolean;
  shares_breakdown: Record<string, number>;
}

// Cap Table Evolution Matrix
export interface CapTableMatrixEntry {
  stakeholder_id: string;
  stakeholder_name: string;
  stakeholder_type: StakeholderType;
  share_class_id: string;
  share_class_name: string;
  shares: number;
  ownership_percentage: number;
}

interface CapTableMatrixTransaction {
  transaction_id: string;
  transaction_date: string;
  transaction_type: TransactionType;
  description?: string;
  round_name?: string;
  entries: CapTableMatrixEntry[];
  total_shares_outstanding: number;
}

export interface CapTableMatrix {
  transactions: CapTableMatrixTransaction[];
  stakeholders: Stakeholder[];
  share_classes: ShareClass[];
  from_date?: string;
  to_date?: string;
}

// Scenario Modeling
export interface CapTableScenario {
  id: string;
  name: string;
  description?: string;
  portfolio_company_id: string;
  base_snapshot_id?: string;
  base_date?: string; // Base date for scenario analysis
  transaction_count: number;
  scenario_transactions?: ScenarioTransaction[]; // Optional for compatibility
  results?: ScenarioResults;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface ScenarioResultPosition {
  stakeholder_id: string;
  stakeholder_name: string;
  ownership_percentage?: number;
  shares?: number;
}

interface ScenarioResults extends Partial<CapTableSnapshot> {
  total_shares_outstanding?: number;
  positions?: ScenarioResultPosition[];
}

export enum TxType {
  FINANCING_ROUND = 'FINANCING_ROUND',
  SAFE_CONVERSION = 'SAFE_CONVERSION',
  NOTE_CONVERSION = 'NOTE_CONVERSION',
  OPTION_EXERCISE = 'OPTION_EXERCISE',
  TRANSFER = 'TRANSFER',
  MANUAL_ADJUSTMENT = 'MANUAL_ADJUSTMENT',
  ISSUANCE = 'ISSUANCE',
  SHARE_ISSUANCE = 'SHARE_ISSUANCE',
  OPTION_GRANT = 'OPTION_GRANT',
}

interface ScenarioTransactionDraft {
  transaction_type: TransactionType;
  transaction_date: string;
  description?: string;
  entries: ScenarioLedgerEntry[];
  round_details?: ScenarioRoundDetails;
}

interface ScenarioTransaction {
  id: string;
  company_id: string;
  tx_type: TxType;
  effective_date: string;
  description?: string | null;
  scenario_id?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  entries: ScenarioLedgerEntry[];
  round_details?: ScenarioRoundDetails;
}

interface ScenarioLedgerEntry {
  stakeholder_id: string;
  share_class_id: string;
  shares_delta: number;
  price_per_share?: number;
}

interface ScenarioRoundDetails {
  round_name?: string;
  price_per_share?: number;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  total_amount?: number;
  investor_commitments?: InvestorCommitment[];
}

interface InvestorCommitment {
  stakeholder_id: string;
  investment_amount: number;
  shares_to_receive: number;
  pro_rata_participation?: number;
}

export interface WaterfallStakeholderPayout {
  stakeholder_id: string;
  stakeholder_name: string;
  stakeholder_type: string;
  pref_payout: number;
  common_payout: number;
  total_payout: number;
  ownership_percentage: number;
  payout_percentage: number;
  is_fully_capped_for_common: boolean;
}

export interface WaterfallStepResult {
  step_name: string;
  amount_distributed: number;
  remaining_value: number;
}

export interface WaterfallResult {
  exit_value: number;
  total_liquidation_preferences: number;
  total_distributed: number;
  remaining_after_preferences: number;
  steps: WaterfallStepResult[];
  stakeholder_proceeds: Record<string, number>;
  total_pref_amount?: number | null;
  remaining_after_prefs?: number | null;
  stakeholder_payouts?: WaterfallStakeholderPayout[] | null;
  summary_by_type?: Record<string, number> | null;
}

export interface WaterfallCalculationRequest {
  exit_value: number;
  scenario_id?: string;
}

// Waterfall Analysis
export interface WaterfallAnalysis {
  id: string;
  scenario_name: string;
  exit_valuation: number;
  analysis_date: string;
  waterfall_tiers: WaterfallTier[];
  net_proceeds_distribution: ProceedsDistribution[];
  summary_metrics: WaterfallSummaryMetrics;
}

interface WaterfallTier {
  tier_rank: number;
  tier_name: string;
  tier_type: string; // liquidation_preference, participation, common
  liquidation_preference?: number;
  participation_multiple?: number;
  is_participating: boolean;
  stakeholder_allocations: StakeholderAllocation[];
}

interface StakeholderAllocation {
  stakeholder_id: string;
  stakeholder_name: string;
  allocation_amount: number;
  allocation_percentage: number;
  shares_basis: number;
  preference_basis?: number;
}

interface ProceedsDistribution {
  stakeholder_id: string;
  stakeholder_name: string;
  stakeholder_type: StakeholderType;
  total_proceeds: number;
  liquidation_preference: number;
  participation_proceeds: number;
  common_proceeds: number;
  total_return_multiple: number;
  irr?: number;
}

interface WaterfallSummaryMetrics {
  total_proceeds: number;
  preference_coverage_ratio: number;
  common_shareholder_proceeds: number;
  preferred_shareholder_proceeds: number;
  founder_proceeds: number;
  employee_proceeds: number;
  common_multiple: number;
  preferred_multiple: number;
}

// API Request/Response Types
export interface CreateStakeholderRequest {
  name: string;
  stakeholder_type: StakeholderType;
  registration_number?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  notes?: string;
  fund_id?: string;
}

export interface UpdateStakeholderRequest {
  name: string;
  stakeholder_type: StakeholderType;
  registration_number?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  notes?: string;
}

export interface CreateShareClassRequest {
  name: string;
  class_type: ShareClassType;
  liquidation_preference?: number;
  liquidation_preference_multiple?: number;
  is_participating?: boolean;
  participation_cap_multiple?: number;
  is_cumulative_dividend?: boolean;
  dividend_rate?: number;
  anti_dilution_protection?: string;
  strike_price?: number;
  exercise_period_years?: number;
  vesting_schedule?: VestingSchedule;
}

export interface CreateTransactionLegalDocument {
  document_id: string;
  document_types?: DocumentType[];
}

export interface CreateTransactionRequest {
  transaction_type: TransactionType;
  transaction_date: string;
  description?: string;
  entries: LedgerEntryRequest[];
  round_name?: string;
  price_per_share?: number;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  total_amount?: number;
  asset_currency_id?: string;
  asset_amount?: number;
  // The strike an option grant fixes, in the units of its own date. A grant
  // that omits it falls back to the OPTIONS class's current strike, which is
  // undated and so cannot be split-adjusted.
  option_strike_price?: number;
  legal_documents?: CreateTransactionLegalDocument[];
}

export interface LedgerEntryRequest {
  stakeholder_id: string;
  share_class_id: string;
  shares_delta: number | null;
  price_per_share?: number;
  certificate_number?: string;
  vesting_start_date?: string;
  vesting_end_date?: string;
  exercise_deadline?: string;
  notes?: string;
}

// The split header (target class + new:old ratio) lives on the transaction;
// there are no per-holder entries to send. The preview is the single source
// of the scaled holder rows.
export interface CreateStockSplitRequest {
  transaction_date: string;
  // Omitted means every class — a whole-company split, the only shape
  // that leaves ownership percentages unmoved.
  share_class_id?: string;
  // Sent as typed, not as a JS number: NUMERIC(20, 8) over the full validated
  // range carries more digits than a double, so Number() would round the
  // ratio before it left the browser.
  ratio_numerator: string;
  ratio_denominator: string;
  legal_documents?: CreateTransactionLegalDocument[];
}

export type UpdateStockSplitRequest = Omit<
  CreateStockSplitRequest,
  'legal_documents'
>;

export interface PreviewStockSplitRequest {
  transaction_date: string;
  // Omitted means every class — a whole-company split, the only shape
  // that leaves ownership percentages unmoved.
  share_class_id?: string;
  // Decimal strings, for the same reason as CreateStockSplitRequest: the
  // preview must be computed from the exact ratio the save will use.
  ratio_numerator: string;
  ratio_denominator: string;
  // Set when previewing an edit so the split's own prior ratio is excluded
  // from the fold instead of being folded into current_shares.
  transaction_id?: string;
}

export interface StockSplitPreviewRow {
  stakeholder_id: string;
  stakeholder_name: string;
  current_shares: number;
  resulting_shares: number;
  /**
   * Whether this holder lands on part of a share. Derived by the backend, not
   * inferred here — the approval gate refuses on the same verdict, and two
   * answers to one question is how they drift apart.
   */
  is_fractional: boolean;
}

// Raw wire shape: current_shares/resulting_shares arrive as Decimal strings.
export interface ApiStockSplitPreviewRow extends Omit<
  StockSplitPreviewRow,
  'current_shares' | 'resulting_shares'
> {
  current_shares: string;
  resulting_shares: string;
}

export interface CreateConvertibleRequest {
  stakeholder_id: string;
  instrument_type: InstrumentType;
  investment_amount: number;
  currency_id: string;
  issue_date: string;
  signing_date?: string;
  transfer_date?: string;
  maturity_date?: string;
  interest_rate?: number;
  interest_method?: InterestMethod;
  interest_accrual_frequency?: AccrualFrequency;
  discount_rate?: number;
  valuation_cap?: number;
  strike_price?: number;
  number_of_shares?: number;
  coverage_percentage?: number;
  document_id?: string;
  most_favored_nation?: boolean;
  pro_rata_rights?: boolean;
  legal_documents?: LegalDocument[];
  notes?: string;
}

export interface CreateScenarioRequest {
  name: string;
  description?: string;
  base_date: string;
  scenario_transactions: (ScenarioTransactionDraft | ScenarioTransaction)[];
}

export interface WaterfallAnalysisRequest {
  scenario_name: string;
  exit_valuation: number;
  exit_type?: string;
  analysis_assumptions?: Record<string, unknown>;
}

// Utility Types
export interface CapTableValidationResult {
  is_valid: boolean;
  errors: CapTableValidationError[];
  warnings: ValidationWarning[];
}

interface CapTableValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
  code: string;
}

interface ValidationWarning {
  field: string;
  message: string;
  code: string;
  suggestion?: string;
}

// Export utility functions type
export interface CapTableExportOptions {
  format: 'csv' | 'xlsx' | 'pdf';
  view_mode: CapTableViewMode;
  include_convertibles: boolean;
  include_options: boolean;
  include_historical: boolean;
  date_range?: {
    from_date: string;
    to_date: string;
  };
  stakeholder_filter?: string[];
  share_class_filter?: string[];
}

// Financing Round Wizard Types
export interface InvestorInvestment {
  investor_name: string;
  investment_amount: number;
  fund_id?: string;
}

export interface FinancingRoundWizardRequest {
  type: 'equity' | 'safe_pre' | 'safe_post' | 'note';
  date: string;
  investment: number; // Total investment amount (sum of all investors)
  investors: InvestorInvestment[]; // Individual investors for all round types
  pre_money?: number;
  option_pool_target_pct?: number;
  option_pool_is_post_money?: boolean; // True = post-money, False = pre-money
  convert_outstanding_safes_notes: boolean;
  // SAFE and Note specific fields
  valuation_cap?: number;
  discount_rate?: number;
  interest_rate?: number;
  signing_date?: string;
  transfer_date?: string;
  maturity_date?: string;
  interest_method?: string;
  interest_accrual_frequency?: string;
  document_id?: string;
  currency_id?: string;
}

export interface ApiSharePosition extends Omit<SharePosition, 'shares'> {
  shares: string;
}

export interface ApiSimpleCapTableSnapshot {
  company_id: string;
  as_of_date: string;
  positions: ApiSharePosition[];
  total_shares_by_class: Record<string, string>;
}

export interface ApiScenarioSnapshot extends Omit<
  ScenarioSnapshot,
  'pre_round' | 'post_round'
> {
  pre_round: ApiSimpleCapTableSnapshot;
  post_round: ApiSimpleCapTableSnapshot;
}

export interface SharePosition {
  stakeholder_id: string;
  stakeholder_name: string;
  share_class_id: string;
  share_class_name: string;
  shares: number;
}

export interface SimpleCapTableSnapshot {
  company_id: string;
  as_of_date: string;
  positions: SharePosition[];
  total_shares_by_class: Record<string, number>;
}

export interface ScenarioSnapshot {
  pre_round: SimpleCapTableSnapshot;
  post_round: SimpleCapTableSnapshot;
  price_per_share?: number;
  new_shares_issued: number;
  option_pool_increase: number;
  converted_securities: unknown[];
}

export interface MaturityResolutionPreview {
  investment_amount: number;
  accrued_interest: number;
  total_value: number;
  implied_shares: number | null;
}

export interface ConvertToSharesAtMaturityRequest {
  price_per_share: number;
  share_class_id: string;
}

export interface ExtendMaturityRequest {
  new_maturity_date: string; // ISO date string YYYY-MM-DD
}

export interface TransactionDocument {
  id: string;
  transaction_id: string;
  document_id: string;
  document_types: DocumentType[];
  document_name: string;
  document_original_filename: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
}

export interface AttachDocumentToTransactionRequest {
  document_id: string;
  document_types?: DocumentType[];
}

// Reconciliation: 2-of-3 source state for the panel on the transaction view.

export enum ReconciliationSource {
  FUND_INPUT = 'fund_input',
  BANK = 'bank',
  LEGAL_DOC = 'legal_doc',
}

export interface ReconciliationSlot {
  source: ReconciliationSource;
  required: boolean;
  verified: boolean;
  artefact_id: string | null;
  artefact_label: string | null;
  actor_name: string | null;
  verified_at: string | null;
}

interface ManualApprovalInfo {
  actor_name: string | null;
  approved_at: string;
  reason: string;
}

export interface ReconciliationState {
  transaction_id: string;
  status: CaptableTransactionStatus;
  threshold: number;
  verified_source_count: number;
  awaiting_fx_rate: boolean;
  accepted_doc_tags: string[];
  slots: ReconciliationSlot[];
  can_manual_approve: boolean;
  manual_approval: ManualApprovalInfo | null;
}

export interface ManualApproveRequest {
  reason: string;
}
interface BankEntryManualFields {
  value_date: string;
  amount: number;
  currency: string;
  counterparty_iban: string;
  payment_reference: string;
}

export interface AddBankEntryRequest {
  fund_transaction_id?: string | null;
  manual_entry: BankEntryManualFields;
}

export type UpdateBankEntryRequest = BankEntryManualFields;

type BankEntrySource = 'MANUAL' | 'PSD2';

export interface BankEntry {
  id: string;
  transaction_id: string;
  // Bank evidence lives on the fund's transaction, never on the cap table.
  fund_transaction_id: string;
  fund_name: string;
  value_date: string;
  amount: number;
  currency: string;
  counterparty_iban: string;
  payment_reference: string | null;
  source: BankEntrySource;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

// One fund's share of a cap table transaction: where its bank evidence lives.
export interface CaptableFundTransaction {
  fund_transaction_id: string;
  fund_id: string;
  fund_name: string;
  transaction_type: string;
  status: string;
  expected_amount: number;
  confirmed_amount: number;
  currency: string;
  bank_verified: boolean;
  accepts_bank_entries: boolean;
}

export interface FinancingRoundResponse {
  id: string;
  portfolio_company_id: string;
  round_type: string;
  name: string;
  date: string;
  status: string;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  transaction_count: number;
}

export type StakeholderInputType = 'existing' | 'external' | 'fund';

export interface PrimaryIssuanceRequest {
  _key?: string; // frontend-only: stable React list key
  stakeholder_id: string;
  share_class_id: string;
  shares: number;
  price_per_share: number;
  date: string;
  currency_id?: string;
  notes?: string; // frontend-only UI field
  description?: string; // sent to backend
}

export interface SecondaryTransferRequest {
  _key?: string; // frontend-only: stable React list key
  seller_stakeholder_id: string;
  buyer_stakeholder_id: string;
  share_class_id: string;
  buyer_share_class_id: string;
  shares: number;
  price_per_share: number;
  cost_basis?: number;
  date: string;
  currency_id?: string;
  notes?: string; // frontend-only UI field
  description?: string; // sent to backend
  earn_outs?: SecondaryTransferEarnOutInput[];
}

interface SecondaryTransferEarnOutInput {
  milestone: string | null;
  target_amount: number | null;
  due_date: string | null;
  fair_value: number | null;
}

export interface ConvertibleRoundRequest {
  stakeholder_id: string;
  instrument_type: InstrumentType;
  investment_amount: number;
  currency_id: string;
  valuation_cap?: number;
  discount_rate?: number;
  mfn?: boolean;
  date: string;
  signing_date?: string;
  maturity_date?: string;
  interest_rate?: number;
  interest_method?: InterestMethod;
  interest_accrual_frequency?: AccrualFrequency;
  notes?: string; // frontend-only UI field
  description?: string; // sent to backend
}

export interface WarrantRoundRequest {
  _key?: string; // frontend-only: stable React list key
  stakeholder_id: string;
  // The class the terms below are denominated in, fixed at grant. A split
  // rescales one class, and adjusting the warrant's entitlement and strike
  // through it is only possible if the class is recorded.
  share_class_id: string;
  strike_price: number;
  number_of_shares: number;
  expiry_date?: string;
  date: string;
  currency_id: string;
  notes?: string; // frontend-only UI field
  description?: string; // sent to backend
}

export type ConvertibleConversionAction = 'convert' | 'repay';

export interface ConvertibleConversionRequest {
  instrument_id: string;
  action: ConvertibleConversionAction;
  share_class_name?: string;
  price_per_share?: number;
  num_shares?: number;
  conversion_amount?: number;
}

export interface WarrantExerciseRoundRequest {
  instrument_id: string;
  share_class_name: string;
  price_per_share: number;
  num_shares: number;
  warrant_conversion_date?: string;
}

export interface DeriveConversionRequest {
  pre_money_valuation: number;
  price_per_share: number;
  conversion_date: string;
}

export interface DeriveConversionResponse {
  principal: number;
  accrued_interest: number;
  total_conversion_amount: number;
  cap_price: number | null;
  discount_price: number | null;
  effective_price: number;
  method_used: 'cap' | 'discount' | 'none';
  implied_shares: number;
}

export type RoundType = 'financing_round' | 'dividend' | 'stock_split';

export interface ComplexRoundRequest {
  name: string;
  date: string;
  round_type?: RoundType;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  description?: string;
  primary_issuances: PrimaryIssuanceRequest[];
  secondary_transfers: SecondaryTransferRequest[];
  convertibles: ConvertibleRoundRequest[];
  warrants: WarrantRoundRequest[];
  convertible_conversions: ConvertibleConversionRequest[];
  warrant_exercises: WarrantExerciseRoundRequest[];
  legal_documents: CreateTransactionLegalDocument[];
}

export interface ApiComplexRoundResponse extends Omit<
  ComplexRoundResponse,
  'pre_round_snapshot' | 'post_round_snapshot'
> {
  pre_round_snapshot: ApiSimpleCapTableSnapshot;
  post_round_snapshot: ApiSimpleCapTableSnapshot;
}

export interface ComplexRoundResponse {
  name: string;
  round_type: RoundType;
  date: string;
  pre_money_valuation?: number;
  post_money_valuation?: number;
  transaction_ids: string[];
  pre_round_snapshot: SimpleCapTableSnapshot;
  post_round_snapshot: SimpleCapTableSnapshot;
}
